import React, { useState } from 'react';

import { StepProps, track, useMutation } from '@betterment/js-runtime';
import { Box, Button, CenteredWorkflowLayout, Flash, Text } from '@betterment/style-closet-react';

import { FlowState } from './Controller';

import { useCustomer } from '../Checking/components/CustomerProvider';
import { AccountCreationRequestCompletionResponse, AccountCreationRequestResponse } from '../Checking/data';
import Footer from '../Shared/components/Footer';
import { urlBuilder } from '../Shared/urlBuilder';

const Confirm: React.FC<React.PropsWithChildren<StepProps<FlowState> & Partial<AccountCreationRequestResponse>>> = ({
  requestId,
  defaultAccountName,
  goToNextStep,
  updateValues,
}) => {
  const [shouldShowFlash, setShowFlash] = useState(false);
  const [createAccount, { loading }] = useMutation<AccountCreationRequestCompletionResponse, { name: string }>(
    urlBuilder.apiV2CheckingAccountCreationRequestsCompletion.absolute({ requestId: requestId! })
  );
  const { refetch } = useCustomer();

  const onContinue = async () => {
    const { data, errors } = await createAccount({ name: defaultAccountName! });

    if (errors) {
      setShowFlash(true);
    } else if (data?.accountId) {
      track('ElementClicked', { name: 'CreateSecondCheckingAccount', flow: 'OpenSecondCheckingAccount' });

      await refetch();
      updateValues({ accountId: data.accountId });
      goToNextStep();
    }
  };

  return (
    <CenteredWorkflowLayout title="Checking signup" heading="Review and confirm">
      <CenteredWorkflowLayout.Slot name="content">
        {shouldShowFlash && (
          <Box mb="9">
            <Flash variant="error">
              <Text color="bmtGrey90">Something went wrong. Please try again.</Text>
            </Flash>
          </Box>
        )}
        <Box as="dl">
          <Box borderTop="1px solid" borderColor="bmtGrey20" pt="6" mt="8">
            <Text as="dt">Account type</Text>
            <Text as="dd" ml="0" fontSize="1" color="bmtGrey50">
              Individual Checking
            </Text>
          </Box>
          <Box borderTop="1px solid" borderBottom="1px solid" borderColor="bmtGrey20" py="6" mt="6" mb="9">
            <Text as="dt">Account name</Text>
            <Text as="dd" ml="0" fontSize="1" color="bmtGrey50">
              {defaultAccountName}
            </Text>
          </Box>
        </Box>
        <CenteredWorkflowLayout.Actions>
          <Button type="button" px="8" disabled={loading} onClick={onContinue}>
            Create account
          </Button>
        </CenteredWorkflowLayout.Actions>
      </CenteredWorkflowLayout.Slot>
      <CenteredWorkflowLayout.Slot name="footer">
        <Footer />
      </CenteredWorkflowLayout.Slot>
    </CenteredWorkflowLayout>
  );
};

export default Confirm;
